import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { filterGamesByCivilRange } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type {
  CivilDateRange,
  GameLike,
} from '@sportninja/common/features/game/gameListView/types'

import type { GameViewMode } from './useGameViewMode'
import type { GameListQueries } from '../utils/buildGameListQuery'

/**
 * How many raw API pages a SCROLLING view chases on its own.
 *
 * Small on purpose. List and Details page by scrolling, exactly as List always
 * has — the user pulls more games in by scrolling toward them. This budget only
 * exists to unstick the one case scrolling cannot fix: after client-side
 * civil-date filtering, an early raw page can contribute zero visible rows, so
 * nothing is scrollable and nothing would ever trigger the next page.
 */
export const RAW_PAGE_AUTO_FOLLOW_CAP = 8

/**
 * Enough rows that the document scrolls, which is what re-arms scroll-driven
 * pagination. Below this the list is stuck.
 */
export const MIN_DISPLAYABLE_ROWS = 12

/**
 * A BOUNDED calendar window — a month, a week, a day — renders all at once and
 * has nothing to scroll, so it loads its window to completion instead. At
 * `per_page: 200` that is one or two requests for a realistic month.
 *
 * This budget is safe only because the window is bounded. Agenda has no
 * viewport of its own and lists whatever the filters select, so it pages by
 * scrolling like List and Details do — see `hasBoundedWindow`.
 */
export const CALENDAR_PAGE_CAP = 25

/** Hard stop, so a pathological dataset cannot spin forever. */
export const LOAD_ALL_PAGE_CAP = 200

interface Pagination {
  current_page?: number
  total_pages?: number
  total?: number
  count?: number
  per_page?: number
}

interface GamesPage {
  data: GameLike[]
  meta?: { pagination?: Pagination }
}

export interface GameListCounts {
  /** Games actually pulled from the API, padding included. */
  rawLoaded: number
  /** Of those, the ones inside the visible civil range. */
  matchingLoaded: number
  /** What pagination claims the PADDED superset holds — never a visible total. */
  rawTotal: number
  /** Pages in the padded superset; 1 means there was never more to fetch. */
  rawTotalPages: number
  hasUnloadedRawPages: boolean
  isComplete: boolean
}

export interface UseGameListItemsArgs {
  slug: string
  queries: GameListQueries
  civilRange: CivilDateRange | null
  enabled: boolean
  /** Bumping this starts a fresh page-one query rather than appending. */
  refreshToken: number
  mode: GameViewMode
  /**
   * True when the view draws a BOUNDED window all at once — a calendar month,
   * week or day. Those load their window to completion because a partially
   * loaded month is simply wrong and there is nothing to scroll.
   *
   * False for every scrolling view, INCLUDING calendar's agenda layout: agenda
   * has no viewport of its own, so its window is the whole filter range and
   * loading it to completion walks a full season 200 games at a time.
   */
  hasBoundedWindow: boolean
}

export interface UseGameListItemsResult {
  games: GameLike[]
  rawGames: GameLike[]
  isLoading: boolean
  isError: boolean
  error: unknown
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  loadAll: () => Promise<void>
  isLoadingAll: boolean
  refetch: () => void
  counts: GameListCounts
  queryKey: unknown[]
  /**
   * True only when we are certain there is nothing to show. While raw pages
   * remain unloaded the answer is "not yet", not "none" — showing the empty
   * state early is the bug that makes a filtered List look permanently blank.
   */
  showEmptyState: boolean
}

export const useGameListItems = ({
  slug,
  queries,
  civilRange,
  enabled,
  refreshToken,
  mode,
  hasBoundedWindow,
}: UseGameListItemsArgs): UseGameListItemsResult => {
  'use memo'

  const queryClient = useQueryClient()
  const [isLoadingAll, setIsLoadingAll] = useState(false)

  const queryKey = queryKeys.scheduleGames.byFingerprint({
    ...queries.fingerprint,
    slug,
    mode,
    refreshToken,
  })

  const query = useInfiniteQuery<GamesPage>({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) =>
      (await req(slug, {
        query: { ...queries.display, page: pageParam as number },
        //  React Query aborts superseded fetches; without threading the signal
        //  a filter change leaves the old request running to completion.
        signal,
      })) as GamesPage,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.meta?.pagination
      const current = Number(pagination?.current_page ?? 0)
      const total = Number(pagination?.total_pages ?? 0)
      return current > 0 && current < total ? current + 1 : undefined
    },
    enabled,
    staleTime: 30_000,
  })

  const pages = query.data?.pages ?? []
  const rawGames = pages.flatMap((page) => page?.data ?? [])
  const games =
    civilRange === null
      ? rawGames
      : filterGamesByCivilRange(rawGames, civilRange)

  const lastPagination = pages[pages.length - 1]?.meta?.pagination
  const rawTotal = Number(lastPagination?.total ?? rawGames.length)
  const rawTotalPages = Number(lastPagination?.total_pages ?? 1)

  const counts: GameListCounts = {
    rawLoaded: rawGames.length,
    matchingLoaded: games.length,
    rawTotal,
    rawTotalPages,
    hasUnloadedRawPages: Boolean(query.hasNextPage),
    isComplete: !query.hasNextPage && !query.isFetching,
  }

  //  A ref, not state: the effect below must not re-run just because the count
  //  changed, or a slow API turns one follow into a render loop.
  const autoFollowedRef = useRef(0)
  const followKeyRef = useRef('')
  const currentFollowKey = JSON.stringify(queryKey)

  if (followKeyRef.current !== currentFollowKey) {
    followKeyRef.current = currentFollowKey
    autoFollowedRef.current = 0
  }

  useEffect(() => {
    if (!enabled || query.isFetching || !query.hasNextPage) {
      return
    }
    //  A bounded calendar window has nothing to scroll — it draws a month, week
    //  or day all at once, so a partially-loaded window would just be wrong. It
    //  loads to completion. Every scrolling view — List, Details and calendar's
    //  agenda — only needs enough rows to make scrolling possible; from there
    //  the user's own scrolling pulls in the rest, exactly as the List view has
    //  always worked.
    const cap = hasBoundedWindow ? CALENDAR_PAGE_CAP : RAW_PAGE_AUTO_FOLLOW_CAP

    if (autoFollowedRef.current >= cap) {
      return
    }

    const needsMore = hasBoundedWindow
      ? true
      : games.length < MIN_DISPLAYABLE_ROWS

    if (!needsMore) {
      return
    }

    autoFollowedRef.current += 1
    //  Deliberately not awaited: React Query captures the outcome in query
    //  state, and query-core already swallows the rejection when `throwOnError`
    //  is unset, so there is nothing here to handle.
    query.fetchNextPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    hasBoundedWindow,
    games.length,
    query.isFetching,
    query.hasNextPage,
    currentFollowKey,
  ])

  const loadAll = async () => {
    if (isLoadingAll) {
      return
    }
    setIsLoadingAll(true)
    try {
      let guard = 0
      // Read the live cache state each round — `query.hasNextPage` is captured
      // from this render and would never flip inside the loop.
      let next = queryClient.getQueryState(queryKey)
      while (guard < LOAD_ALL_PAGE_CAP) {
        const result = await query.fetchNextPage()
        guard += 1
        if (!result.hasNextPage) {
          break
        }
        next = queryClient.getQueryState(queryKey)
        if (next?.status === 'error') {
          break
        }
      }
    } finally {
      setIsLoadingAll(false)
    }
  }

  return {
    games,
    rawGames,
    // A DISABLED query is not "loading" to react-query — `isLoading` is
    // `isPending && isFetching`, and a gated query fetches nothing, so it
    // reports false while the filters/period that gate it are still resolving.
    // Treated as loading here: the caller is waiting on data either way, and
    // without this the view falls through to its empty state for a frame
    // before the real fetch starts (empty flash → skeleton → list).
    isLoading: query.isLoading || !enabled,
    isError: query.isError,
    error: query.error,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage: () => {
      if (!query.isFetching && query.hasNextPage) {
        query.fetchNextPage()
      }
    },
    loadAll,
    isLoadingAll,
    //  A failed page has to be retryable from the view that reports it.
    refetch: () => {
      query.refetch()
    },
    counts,
    queryKey,
    // `enabled` for the same reason as `isLoading` above — a query that has
    // not been allowed to run yet knows nothing, which is not the same as
    // knowing there is nothing.
    showEmptyState:
      enabled &&
      !query.isLoading &&
      !query.isFetching &&
      games.length === 0 &&
      !query.hasNextPage,
  }
}
